// HUD CSS lives here so HudPanel.ts stays under the file-size cap (Rule 15).
// One injection per page; idempotent on repeat calls. No runtime state — purely
// static stylesheet text.

let stylesInjected = false;

export function injectHudStyles(): void {
  if (stylesInjected) return;
  if (typeof document === 'undefined') return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = HUD_CSS;
  document.head.appendChild(style);
}

const HUD_CSS = `
#hud { position: absolute; inset: 0; pointer-events: none; z-index: 10; }
.hud__resources {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 16px;
  background: linear-gradient(180deg, rgba(20,28,38,0.85), rgba(10,14,20,0.9));
  border: 1px solid #2c3a48;
  border-radius: 0;
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  box-shadow: 0 0 0 1px rgba(106,216,255,0.06), 0 6px 20px rgba(0,0,0,0.5), inset 0 0 18px rgba(58,123,213,0.06);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
  pointer-events: none;
  min-width: 190px;
}
.hud-stat { display: flex; justify-content: space-between; gap: 16px; align-items: baseline; }
.hud-stat__label { color: #6ad8ff; font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; opacity: 0.78; }
.hud-stat__value { color: #cfd6dd; font-size: 13px; }
.hud-stat__value.hud-stat__primary {
  color: #ffe680;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-shadow: 0 0 8px rgba(255,230,128,0.45);
}
/* V1.7 (3.17): at-cap squad readout. Tints the row red when
 * squadCount + queue ≥ squadCap so the player notices production is about
 * to stall. Matches the .hud__timer urgent palette for visual continuity. */
.hud-stat__value[data-at-cap="true"] {
  color: #ff8a70;
  text-shadow: 0 0 8px rgba(255,138,112,0.45);
}
.hud__timer {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 28px;
  background: linear-gradient(180deg, rgba(20,28,38,0.9), rgba(10,14,20,0.92));
  border: 1px solid #2c3a48;
  border-radius: 0;
  clip-path: polygon(12px 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 12px 100%, 0 50%);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 28px;
  letter-spacing: 0.16em;
  color: #eef3f8;
  pointer-events: none;
  text-shadow: 0 0 10px rgba(106,216,255,0.4);
  box-shadow: 0 0 18px rgba(106,216,255,0.18);
}
.hud__timer[data-urgent="true"] {
  color: #ff8a70;
  border-color: #e2543c;
  text-shadow: 0 0 12px rgba(255,138,112,0.6);
  box-shadow: 0 0 24px rgba(226,84,60,0.4);
  animation: hud-timer-flash 0.7s steps(2, end) infinite;
}
.hud__banner {
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 14px;
  background: rgba(245,231,110,0.15);
  border: 1px solid #f5e76e;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  letter-spacing: 0.12em;
  color: #f5e76e;
  pointer-events: none;
  text-transform: uppercase;
}
.hud__overtime {
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  padding: 5px 16px;
  background: rgba(226,84,60,0.18);
  border: 1px solid #e2543c;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  letter-spacing: 0.14em;
  color: #ffb3a3;
  pointer-events: none;
  text-transform: uppercase;
  animation: hud-timer-flash 0.7s steps(2, end) infinite;
}
.hud-surge {
  margin-top: 4px;
  padding: 2px 8px;
  background: rgba(74,142,208,0.18);
  border: 1px solid #4a8ed0;
  border-radius: 3px;
  color: #9ec8f0;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-align: center;
  text-transform: uppercase;
  pointer-events: none;
}
.hud-enemy {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 14px;
  background: linear-gradient(180deg, rgba(20,28,38,0.85), rgba(10,14,20,0.9));
  border: 1px solid #2c3a48;
  border-radius: 0;
  clip-path: polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px));
  box-shadow: 0 0 0 1px rgba(213,90,58,0.10), 0 6px 18px rgba(0,0,0,0.5);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
  pointer-events: none;
  min-width: 150px;
}
.hud-enemy__title {
  font-size: 10px;
  letter-spacing: 0.18em;
  color: #ff9670;
  text-transform: uppercase;
  opacity: 0.85;
}
.hud-surge--enemy {
  margin-top: 0;
}
.hud-doctrine {
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-align: center;
  text-transform: uppercase;
  pointer-events: none;
  background: rgba(245,231,110,0.12);
  border: 1px solid #b3a64a;
  color: #f5e76e;
}
.hud-doctrine[data-doctrine="saboteur"] {
  background: rgba(226,84,60,0.15);
  border-color: #e2543c;
  color: #ffb3a3;
}
.hud-doctrine[data-doctrine="bulwark"] {
  background: rgba(74,142,208,0.18);
  border-color: #4a8ed0;
  color: #9ec8f0;
}
.hud-doctrine--enemy {
  margin-top: 4px;
}
@keyframes hud-timer-flash {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.55; }
}
/* V1.7 — inline term tooltips opt-in. Bubble rendering / positioning is
 * handled by the JS portal in HudTooltip.ts (sidesteps the resource
 * panel's clip-path which would otherwise crop a CSS ::after bubble).
 * This rule only re-enables pointer events for the tooltip-bearing
 * children so they fire mouseover while their parent .hud__resources
 * keeps pointer-events: none. cursor: help signals "this label has
 * extra info on hover" without any other visual treatment. Touch
 * profile keeps cursor: default since hover isn't a real gesture. */
#hud [data-tooltip] {
  pointer-events: auto;
  cursor: help;
}
/* V1.7 (2.11) — buttons with tooltips are still actionable clickables;
 * the help cursor would mislead users into thinking they're info-only.
 * Keep pointer (or not-allowed when disabled) as the affordance. */
#hud button[data-tooltip]:not(:disabled) { cursor: pointer; }
#hud button[data-tooltip]:disabled { cursor: not-allowed; }
@media (pointer: coarse) {
  #hud [data-tooltip] { cursor: default; }
}
@keyframes hud-ability-ready-pulse {
  0%, 100% { box-shadow: 0 0 4px rgba(74,208,107,0.35); }
  50% { box-shadow: 0 0 10px rgba(74,208,107,0.75); }
}
.hud__context {
  position: absolute;
  right: 16px;
  bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  pointer-events: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
}
.hud-context__empty {
  background: rgba(0,0,0,0.45);
  border: 1px solid #2a3038;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 11px;
  color: #8c95a0;
  pointer-events: auto;
}
/* V5+ — "Select All Squads (n)" CTA. Replaces the empty hint text when the
 * player has 2+ own squads. Styled as a prominent clickable chip so it reads
 * as actionable; mobile-RTS equivalent of desktop's box-drag. */
.hud-context__select-all {
  font-family: inherit;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 8px 14px;
  background: linear-gradient(180deg, rgba(36,46,60,0.95), rgba(20,28,38,0.95));
  color: #f5e76e;
  border: 1px solid #c8b840;
  border-radius: 4px;
  cursor: pointer;
  min-height: 36px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 0 8px rgba(245,231,110,0.18);
  transition: border-color 120ms, color 120ms, background 120ms;
}
.hud-context__select-all:hover:not(:disabled) {
  border-color: #f5e76e; color: #fff7a8;
  background: linear-gradient(180deg, rgba(48,40,16,0.95), rgba(28,24,8,0.95));
  box-shadow: 0 0 12px rgba(245,231,110,0.45);
}
.hud-context__select-all:active:not(:disabled) { transform: scale(0.96); }
.hud-context__panel {
  background: linear-gradient(180deg, rgba(20,28,38,0.94), rgba(10,14,20,0.96));
  border: 1px solid #2c3a48;
  border-radius: 0;
  clip-path: polygon(0 12px, 12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%);
  padding: 12px 16px;
  pointer-events: auto;
  min-width: 270px;
  box-shadow: 0 0 0 1px rgba(106,216,255,0.06), 0 10px 28px rgba(0,0,0,0.55), inset 0 0 18px rgba(58,123,213,0.05);
}
/* V1.6 — squad card vs structure (core/hub) card differentiation. Same shape
 * + position so the spatial expectation doesn't move, but the trim color +
 * a per-card kind label make the two reads instantly distinct. Squad = cyan
 * "TACTICAL" trim; Core/Hub = amber "ECONOMY" trim. */
.hud-context__panel--squad {
  border-color: #2c4868;
  border-left: 3px solid #4a96ff;
  box-shadow: 0 0 0 1px rgba(74,150,255,0.12), 0 10px 28px rgba(0,0,0,0.55), inset 0 0 18px rgba(74,150,255,0.06);
}
.hud-context__panel--core {
  border-color: #4a4020;
  border-left: 3px solid #f5c640;
  box-shadow: 0 0 0 1px rgba(245,198,64,0.10), 0 10px 28px rgba(0,0,0,0.55), inset 0 0 18px rgba(245,198,64,0.06);
  background: linear-gradient(180deg, rgba(28,24,16,0.94), rgba(14,12,8,0.96));
}
.hud-context__title {
  font-size: 11px;
  letter-spacing: 0.16em;
  color: #6ad8ff;
  margin-bottom: 8px;
  text-transform: uppercase;
  text-shadow: 0 0 6px rgba(106,216,255,0.35);
}
.hud-context__panel--squad .hud-context__title { color: #a8d2ff; text-shadow: 0 0 6px rgba(74,150,255,0.45); }
.hud-context__panel--core .hud-context__title { color: #f5c640; text-shadow: 0 0 6px rgba(245,198,64,0.45); }
/* Small kind-label chip in the top-right of each card. Provides a one-word
 * banner that names what the card represents — useful for new players who
 * mistake "Core production tiles" for "selected unit's loadout". */
.hud-context__panel { position: relative; }
.hud-context__panel::after {
  content: '';
  position: absolute;
  top: 8px;
  right: 14px;
  font-size: 9px;
  letter-spacing: 0.14em;
  font-weight: 700;
  text-transform: uppercase;
  opacity: 0.8;
  pointer-events: none;
}
.hud-context__panel--squad::after { content: 'TACTICAL'; color: #a8d2ff; }
.hud-context__panel--core::after { content: 'ECONOMY'; color: #f5c640; }
/* V4 command-card styles (portrait, command grid, production tiles, build
 * row) live in hudCommandCardStyles.ts to keep this file under the 600-line
 * per-file cap. They are injected in parallel by HudPanel. */
.hud-context__hp-row { display: flex; align-items: center; gap: 8px; }
.hud-context__hp-bar {
  flex: 1;
  height: 8px;
  background: #171a1f;
  border: 1px solid #2a3038;
  border-radius: 2px;
  overflow: hidden;
}
.hud-context__hp-fill { height: 100%; width: 100%; background: #4ad06b; transition: width 80ms linear; }
.hud-context__hp-fill[data-low="true"] { background: #e2543c; }
.hud-context__hp-text { font-size: 12px; min-width: 80px; text-align: right; }
.hud-context__stats { display: flex; gap: 16px; font-size: 12px; margin-bottom: 8px; color: #b8c0c8; }
.hud-context__buttons { display: flex; gap: 8px; }
.hud-context__btn {
  flex: 1;
  background: linear-gradient(180deg, rgba(36,46,60,0.9), rgba(20,28,38,0.95));
  color: #eef3f8;
  border: 1px solid #3a4c60;
  border-radius: 0;
  padding: 9px 10px;
  font-family: inherit;
  font-size: 12px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 120ms, color 120ms;
}
.hud-context__btn:hover:not(:disabled) { background: linear-gradient(180deg, rgba(42,58,76,0.95), rgba(24,32,42,0.95)); border-color: #6ad8ff; color: #6ad8ff; }
.hud-context__btn:disabled { opacity: 0.45; cursor: not-allowed; }
.hud-context__queue { margin-top: 8px; margin-bottom: 4px; font-size: 11px; color: #8c95a0; }
.hud-context__queue-bar {
  height: 6px;
  background: #171a1f;
  border: 1px solid #2a3038;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}
.hud-context__queue-bar-fill {
  height: 100%;
  width: 0%;
  background: #f5c842;
  transition: width 80ms linear;
}
.hud-context__queue-bar-fill[data-active="true"] { background: #f5c842; }
.hud-context__queue-bar-fill[data-active="false"] { background: #2a3038; }
.hud-context__queue-bar-fill[data-archetype="recon"]    { background: #6ad8ff; }
.hud-context__queue-bar-fill[data-archetype="assault"]  { background: #ff8060; }
.hud-context__queue-bar-fill[data-archetype="engineer"] { background: #ffe680; }
.hud-context__queue-bar-fill[data-archetype="shield"]   { background: #4ad0b8; }
/* Signal resource bar — thin fill below the Signal stat row. Fills 0→100%
 * where 100% = shield squad cost (140 signal), the most expensive unit.
 * Gives a quick "economic capacity" read without replacing the precise number. */
.hud-signal-bar {
  height: 3px;
  background: #171a1f;
  border: 1px solid #1e2830;
  border-radius: 1px;
  overflow: hidden;
  margin-bottom: 2px;
}
.hud-signal-bar__fill {
  height: 100%;
  width: 0%;
  background: #6ad8ff;
  transition: width 200ms linear;
  opacity: 0.7;
}
.hud-context__ability-row { margin-top: 10px; }
.hud-context__ability-row[data-disabled="true"] { opacity: 0.55; }
.hud-context__ability-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 12px;
  margin-bottom: 4px;
}
.hud-context__ability-name { color: #cfd6dd; }
.hud-context__ability-hotkey {
  font-size: 11px;
  letter-spacing: 0.06em;
  color: #f5e76e;
  border: 1px solid #f5e76e;
  border-radius: 3px;
  padding: 1px 6px;
}
.hud-context__cd-bar { background: #171a1f; border-color: #2a3038; }
.hud-context__cd-fill {
  height: 100%;
  width: 0%;
  background: #4a8ed0;
  transition: width 80ms linear;
}
.hud-context__cd-fill[data-ready="true"] {
  background: #4ad06b;
  animation: hud-ability-ready-pulse 1.5s ease-in-out infinite;
}

/* ── Archetype-coloured production buttons ──────────────────────────────
 * Each squad type gets a subtle accent so players can tap the right button
 * without reading the label under time pressure. Colours mirror the squad
 * icon tints used on the game canvas.
 */
.hud-context__btn[data-archetype="recon"] { border-color: #2a5070; color: #9ec8f0; }
.hud-context__btn[data-archetype="recon"]:hover:not(:disabled) {
  border-color: #6ad8ff; color: #6ad8ff;
  background: linear-gradient(180deg, rgba(30,60,90,0.95), rgba(16,32,50,0.95));
}
.hud-context__btn[data-archetype="assault"] { border-color: #60281a; color: #ff9670; }
.hud-context__btn[data-archetype="assault"]:hover:not(:disabled) {
  border-color: #e2543c; color: #ff8060;
  background: linear-gradient(180deg, rgba(72,28,18,0.95), rgba(36,14,8,0.95));
}
.hud-context__btn[data-archetype="engineer"] { border-color: #4a4010; color: #ffe680; }
.hud-context__btn[data-archetype="engineer"]:hover:not(:disabled) {
  border-color: #f5e76e; color: #f5e76e;
  background: linear-gradient(180deg, rgba(54,44,12,0.95), rgba(26,22,6,0.95));
}
.hud-context__btn[data-archetype="shield"] { border-color: #1a5048; color: #6ed4c0; }
.hud-context__btn[data-archetype="shield"]:hover:not(:disabled) {
  border-color: #4ad0b8; color: #4ad0b8;
  background: linear-gradient(180deg, rgba(16,56,48,0.95), rgba(8,28,24,0.95));
}
/* Press feedback */
.hud-context__btn:active:not(:disabled) { transform: scale(0.96); }


/* ───── V3 Mobile responsive overrides ────────────────────────────────────
 * Triggered when the viewport is narrow OR the primary input is a coarse
 * pointer (finger / stylus). Both conditions need a single layout flip so
 * we use a media-query OR. Desktop with wide windows keeps the original
 * V0.4 layout; touch / phone shifts to a denser, thumb-friendly arrangement
 * that doesn't overlap on portrait viewports.
 */
@media (max-width: 820px), (pointer: coarse) {
  .hud__resources {
    top: 6px;
    left: 6px;
    padding: 6px 8px;
    min-width: 0;
    gap: 2px;
    font-size: 12px;
    border-radius: 3px;
  }
  .hud-stat { gap: 8px; }
  .hud-stat__label { font-size: 9px; letter-spacing: 0.06em; }
  .hud-stat__value { font-size: 12px; }
  .hud-stat__value.hud-stat__primary { font-size: 15px; }
  .hud-surge { font-size: 9px; padding: 1px 4px; }
  .hud-doctrine { font-size: 9px; padding: 1px 4px; }
  .hud__timer {
    top: 4px;
    padding: 3px 12px;
    font-size: 18px;
    letter-spacing: 0.08em;
    border-radius: 3px;
  }
  .hud__banner { top: 38px; font-size: 10px; padding: 2px 8px; }
  .hud__overtime { top: 38px; font-size: 10px; padding: 3px 10px; }
  .hud-enemy {
    top: 6px;
    right: 6px;
    padding: 4px 8px;
    min-width: 0;
    font-size: 10px;
    border-radius: 3px;
  }
  .hud-enemy__title { font-size: 9px; }
  .hud__context {
    /* V5++ — MobileStatusStrip takes over presenting selection info on
     * mobile (squad portrait + HP, Core/Hub title + stats + queue, or the
     * Select-All CTA). Hide the floating HUD context panel entirely on
     * mobile to eliminate the cognitive ping-pong between bottom-right
     * panel and bottom-center action bar. Desktop layout is unchanged. */
    display: none !important;
  }
  .hud-context__panel {
    min-width: 0;
    padding: 6px 8px;
    border-radius: 4px;
    /* Cap the width so the panel never exceeds the viewport minus the
     * minimap + bar gutters. */
    max-width: calc(100vw - 156px);
  }
  /* V5 — squad command grid duplicates MobileActionBar's Attack/Stop/Q/E
   * buttons. Hide on mobile so the panel is just portrait + HP for squads. */
  .hud-context__panel--squad .hud-context__commands { display: none; }
  /* V5+ — Production buttons inside the Core/Hub panel are now duplicated by
   * MobileActionBar's contextual production tiles (the bar swaps Advance/Q/E/
   * Stop for the four archetypes when own Core/Hub is selected). Hide the
   * in-card row on mobile so the panel shows just stats + queue + build-row
   * — the action bar carries the production verbs. */
  .hud-context__panel--core .hud-context__buttons { display: none; }
  /* V5 — Build-row inside the Core/Hub panel is the only Hub/Turret picker on
   * mobile (the standalone BuildPanel is hidden). Compact it. */
  .hud-context__build-section { margin-top: 6px; padding-top: 6px; }
  .hud-context__build-title { font-size: 9px; margin-bottom: 3px; }
  .hud-context__title { font-size: 10px; margin-bottom: 4px; }
  .hud-context__hp-text { font-size: 11px; min-width: 64px; }
  .hud-context__stats { font-size: 11px; gap: 10px; margin-bottom: 4px; }
  /* V4 production button sizing — replaced inline tweaks with the
   * archetype-tile rules defined in hudCommandCardStyles. The :active scale
   * applies here so the press feedback stays unified for both desktop and
   * mobile contexts. */
  .hud-context__btn:active:not(:disabled) { transform: scale(0.96); }
  .hud-context__queue { font-size: 10px; }
  .hud-context__ability-row { margin-top: 6px; }
  .hud-context__ability-header { font-size: 11px; }
  .hud-context__ability-hotkey { font-size: 10px; padding: 1px 4px; }
  .hud-context__empty { font-size: 10px; padding: 4px 8px; }
}
@media (max-width: 460px) {
  /* Phone-portrait: hide the empty *hint text* (the player gets that info
   * from the highlighted squad / minimap) but KEEP the Select-All CTA when
   * it's visible — mobile players don't have desktop's box-drag, so this
   * button is one of the few ways to grab everything at once. Also cap the
   * context panel tighter so it never collides with the minimap. */
  .hud-context__empty-hint { display: none; }
  .hud-context__empty { background: transparent; border: none; padding: 0; }
  .hud-context__panel { max-width: calc(100vw - 140px); }
}
/* Landscape phones — short, wide viewports. The top resource panels + the
 * bottom HUD context + the action bar together can eat the entire screen
 * height on a 360-px-tall phone. Compact every layer so the playable
 * battlefield still has room. */
@media (orientation: landscape) and (max-height: 480px) and (pointer: coarse) {
  .hud__resources { top: 4px; left: 4px; padding: 4px 6px; gap: 1px; }
  .hud-stat__label { font-size: 8px; }
  .hud-stat__value { font-size: 11px; }
  .hud-stat__value.hud-stat__primary { font-size: 13px; }
  .hud-enemy { top: 4px; right: 4px; padding: 3px 6px; font-size: 9px; }
  .hud-enemy__title { font-size: 8px; }
  .hud__timer { top: 2px; padding: 2px 10px; font-size: 14px; }
  .hud__banner, .hud__overtime { top: 28px; font-size: 9px; padding: 1px 6px; }
  /* V5++ — landscape too: the strip handles selection info. */
  .hud__context { display: none !important; }
  /* Reserve room for the minimap on the left + the bar centered + a small
   * gutter so the context panel never collides with either. */
  .hud-context__panel { max-width: calc(100vw - 200px); padding: 4px 6px; }
  .hud-context__title { font-size: 9px; margin-bottom: 2px; }
  .hud-context__stats { font-size: 10px; gap: 8px; margin-bottom: 2px; }
  .hud-context__btn { min-height: 36px; padding: 4px 6px; font-size: 10px; }
  .hud-context__ability-row { margin-top: 3px; }
  .hud-context__empty-hint { display: none; }
  .hud-context__empty { background: transparent; border: none; padding: 0; }
  .hud-context__select-all { font-size: 11px; padding: 6px 10px; min-height: 32px; }
}

`;
